import {
	Breakpoints,
	BreakpointWrapper,
	Container,
	ContainerPadding,
	Text,
	TextAlignment,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Store } from 'redux';
import {
	createEapiAppointment,
	deleteEapiAppointment,
	getEapiCarlines,
	oneformBFFNdpsSend,
} from 'src/bff';
import { getDealerKvps } from 'src/bff/api/get-dealer-kvps';
import {
	DetalleMotivo,
	Email,
	Error,
	Kilometros,
	Loading,
	MatriculaAnoMatriculacionModelo,
	MultipleCTASelection,
	NombreApellidos,
	Telefono,
} from 'src/components';
import { InputRegex } from 'src/components/inputs/InputRegex';
import {
	formatPrefix,
	LayoutTemplate,
	removeWhiteSpace,
	useFaContent,
	useGetWaidLitera,
	useOneFormContext,
} from 'src/feature-app';
import { CTAsSiNo } from 'src/feature-app/InputsValues';
import { getParamsForm } from 'src/feature-app/get-params-form';
import {
	useGetBrand,
	useGetDealers,
	useIsComerciales,
} from 'src/feature-app/hooks';
import { MapaCita, MapaCitaDealer } from 'src/forms/CitaPosventa/MapaCita';
import { formDataBuilder } from 'src/forms/format-fields';
import {
	removeMultipleStepsAndResetScreenIndex,
	removeStep,
	resetStepsScreenIndex,
} from 'src/forms/helpers/add-remove-steps';
import { transformDate } from 'src/helpers';
import {
	CarlineBase,
	DealerInfo,
	DealersData,
	DmsInfo,
	FormDataFields,
	FormDataGroup,
	FormInfo,
	OneFormState,
	Steps,
} from 'src/types';
import { NuevaCitaPosventaFormDefault, NuevaCitaPosventaFormLogged } from '..';
import { CitaFormDataFields } from '../types';
import { ConfirmacionCita } from './ConfirmacionCita';

export enum CitaPosventaSteps {
	Motivo,
	MiembroTheOriginals,
	ChequeoSeguridad,
	MasInformacion,
	Mapa,
	NombreApellido,
	Matricula,
	Kilometros,
	Telefono,
	Email,
	CocheSustitucion,
	CocheAlTaller,
	Confirmacion,
}

interface NuevaCitaPosventaFormProps {
	isCitaPosventaDealer?: boolean;
	isCitaPosventaEmbeddedCalculadora?: boolean;
	isCitaPosventaEmbeddedBonos?: boolean;
	isCitaPosventaEmbeddedBeneficios?: boolean;
	isCitaPosventaEmbeddedMaintenance?: boolean;
	isCitaPosventaEmbeddedPortal?: boolean;
	updatedFormData?: Partial<FormDataGroup>;
	updatedFormInfo?: Partial<FormInfo>;
}

const spliceSteps = (steps: Steps[], step: string, stepName: string) => {
	if (step) {
		const index = steps.findIndex(step => step.name === stepName);
		steps.splice(index, 1);
		return steps;
	}
	return steps;
};

function updateTitle(detalles: any): any {
	return (
		<>
			<Text bold>¿Quieres contarnos algo más </Text>sobre{' '}
			{detalles ? detalles : 'el motivo de tu visita?'}?
		</>
	);
}

const getFormName = (
	isCitaPosventaDealer: boolean,
	isCitaPosventaEmbeddedCalculadora: boolean,
	isCitaPosventaEmbeddedBonos: boolean,
	isCitaPosventaEmbeddedBeneficios: boolean,
	isCitaPosventaEmbeddedMaintenance: boolean,
) => {
	return isCitaPosventaDealer
		? 'cita-posventa-dealer'
		: isCitaPosventaEmbeddedCalculadora
		? 'cita-posventa-calculadora'
		: isCitaPosventaEmbeddedBonos
		? 'cita-posventa-bono'
		: isCitaPosventaEmbeddedBeneficios
		? 'cita-posventa-beneficios'
		: isCitaPosventaEmbeddedMaintenance
		? 'cita-posventa-maintenance'
		: 'cita-posventa';
};

// importante usar OTROS-* para correcto uso del metodo isFakeMotivo(motivo)
export const fakeMotivoValues = {
	softwareId: 'OTROS-SOFTWARE_ID',
	placaOriginals: 'OTROS-PLACA_THE_ORIGINALS',
};

export const CTAsMotivos = (isComerciales: boolean) => [
	{
		label: 'Mantenimiento',
		value: 'MANTENIMIENTO',
		detalles: 'la revisión de mantenimiento',
		detallesPrevStep: 'Revisión de mantenimiento',
	},
	{
		label: 'Neumáticos',
		value: 'NEUMATICOS',
		detalles: 'el cambio de neumáticos',
		detallesPrevStep: 'Cambio de neumáticos',
	},
	{
		label: 'Avería',
		value: 'REPARACION',
		detalles: 'la avería',
		detallesPrevStep: 'Reparación de avería',
	},
	{
		label: 'Revisión pre-ITV',
		value: 'PREITV',
		detalles: (
			<>
				la revisión{' '}
				<BreakpointWrapper min={Breakpoints.b960}>
					<br />
				</BreakpointWrapper>
				pre-ITV
			</>
		),
		detallesPrevStep: 'Revisión pre-ITV',
	},
	{
		label: 'Chapa y pintura',
		value: 'CARROCERIA',
		detalles: 'la reparación de chapa y pintura',
		detallesPrevStep: 'Reparación de chapa y pintura',
	},
	{
		label: 'Accesorios',
		value: 'ACCESORIOS',
		detalles: 'la instalación de los accesorios',
		detallesPrevStep: 'Instalación de accesorios',
	},
	{
		label: 'Actualización Software ID',
		value: fakeMotivoValues.softwareId,
		detalles: 'Actualización Software ID',
		detallesPrevStep: 'Actualización Software ID',
	},
	...(isComerciales
		? [
				{
					label: 'Instalación placa The Originals',
					value: fakeMotivoValues.placaOriginals,
					detalles: 'Instalación placa The Originals',
					detallesPrevStep: 'Instalación placa The Originals',
				},
		  ]
		: []),
	{
		label: 'Otros',
		value: 'OTROS',
		detalles: 'el motivo de tu visita',
		detallesPrevStep: 'Visita',
	},
];

export const NuevaCitaPosventaFormController = ({
	isCitaPosventaDealer,
	isCitaPosventaEmbeddedCalculadora,
	isCitaPosventaEmbeddedBonos,
	isCitaPosventaEmbeddedBeneficios,
	isCitaPosventaEmbeddedMaintenance,
	isCitaPosventaEmbeddedPortal,
	updatedFormData,
}: NuevaCitaPosventaFormProps) => {
	const { nextScreenIndex } = useOneFormContext();
	const { formData, formInfo } = useSelector((state: OneFormState) => state);
	const { fields } = formData;
	const {
		selectedDealer,
		loading: loadingDealers,
		error: errorDealers,
	} = useGetDealers();
	const { userIsAuthenticated } = formInfo;
	const dispatch = useDispatch();
	const store: Store<OneFormState> = useStore();
	const faContent = useFaContent();
	const [loading, setLoading] = useState(true);
	const [steps, setSteps] = useState([]);
	const [firstStep, setFirstStep] = useState(null);
	const [stepsHistory, setStepsHistory] = useState(null);
	const isComerciales = useIsComerciales();
	const groupBrand = useGetBrand();
	const waid = useGetWaidLitera();
	const urlParams = new URLSearchParams(window.location.search);
	const wa = urlParams.get('wa');
	const useLoggedJourney = false; // De momento no es necesario usar todas las funciones del journey logueado/no logueado, tan solo el fill de los datos
	const CTAsMotivo = CTAsMotivos(isComerciales);

	const CitaPosventaData: Partial<FormDataGroup> = {
		headers: {
			DESC_BRAND: 'VW',
			DESC_CAMPAIGNBLOCK: '7011n000000Ajm0AAC',
			DESC_FORMOBJECTIVE: 'Call Me Now',
			DESC_ID_CAMPAIGN: '9. VW LEM',
			DESC_TOKEN: 'VW_DDB_FRM_CALL_ME',
			DESC_VN_VO: 'VN',
			DESC_GDPR_ADAPTED: 'true',
			...updatedFormData?.headers,
		},
		fields: {
			formName: getFormName(
				isCitaPosventaDealer,
				isCitaPosventaEmbeddedCalculadora,
				isCitaPosventaEmbeddedBonos,
				isCitaPosventaEmbeddedBeneficios,
				isCitaPosventaEmbeddedMaintenance,
			),
			formType: 'servicerequest',
			pageCategory: 'Cita Posventa',
			...updatedFormData?.fields,
		},
	};
	const [citaPosventaData, setCitaPosventaData] = useState(CitaPosventaData);

	// Lógica común a los formularios independientemente del estado del login.
	const getMotivoTitle = (): JSX.Element => {
		if (!fields.motivo)
			return (
				<>
					<Text bold>¿Quieres contarnos algo más </Text>sobre el
					motivo de tu{' '}
					<span style={{ whiteSpace: 'nowrap' }}>visita?</span>
				</>
			);
		else {
			const selectedMotivo = CTAsMotivo.find(
				ctaMotivo => ctaMotivo.value === fields.motivo,
			);
			const detalles = selectedMotivo
				? selectedMotivo.detalles
				: 'string';
			return (
				<>
					<Text bold>¿Quieres contarnos algo más </Text>sobre{' '}
					{detalles ? detalles : 'el motivo de tu visita?'}?
				</>
			);
		}
	};

	const updateSteps = (steps: Steps[]) => {
		dispatch({ type: 'UPDATE_STEPS', payload: steps });
		dispatch({
			type: 'UPDATE_LAST_STEP',
			payload: steps[steps.length - 1],
		});
		dispatch({ type: 'SET_NUMBER_OF_SCREENS', payload: steps.length });
	};

	const isPlacaTheOriginalsMotivo = (motivo: string) => {
		return fakeMotivoValues.placaOriginals == motivo;
	};

	const isFakeMotivo = (motivo: string) => {
		return motivo?.substring(0, 6) == 'OTROS-';
	};

	const getFilteredValueMotivo = (motivo: string) => {
		// Para los casos OTROS-* devolver solo OTROS
		return isFakeMotivo(motivo) ? 'OTROS' : motivo;
	};

	const getFilteredLabelMotivo = (motivo: string) => {
		// motivo literal excepto si OTROS-* devolvemos Otros
		if (isFakeMotivo(motivo)) {
			return 'Otros';
		}
		return CTAsMotivo.find(ctaMotivo => ctaMotivo.value === motivo)?.label;
	};

	useEffect(() => {
		const selectedMotivo = CTAsMotivo.find(
			ctaMotivo => ctaMotivo.value === fields.motivo,
		);
		const { steps: actualSteps, screenIndex } = store.getState().formInfo;
		let newSteps = [...steps];

		const m = faContent?.motivo ? faContent.motivo : fields.motivo;

		if (!isPlacaTheOriginalsMotivo(m)) {
			newSteps = removeStep(newSteps, 'MiembroTheOriginals');
			newSteps = removeStep(newSteps, 'ChequeoSeguridad');
		} else if (fields.chequeoSeguridad == 'false') {
			newSteps = removeStep(newSteps, 'CocheSustitucion');
			newSteps = removeStep(newSteps, 'CocheAlTaller');
		}
		updateSteps(newSteps);

		if (isFakeMotivo(m)) {
			newSteps = removeStep(newSteps, 'MasInformacion');
			updateSteps(newSteps);
		} else if (selectedMotivo && actualSteps.length < defaultSteps.length) {
			updateSteps(newSteps);
		}

		if (selectedMotivo && formInfo?.steps && !isFakeMotivo(m)) {
			const formSteps = formInfo.steps;
			const masInformacionIndex = formSteps.findIndex(
				step => step.name === 'MasInformacion',
			);
			if (masInformacionIndex !== -1) {
				formSteps[masInformacionIndex].title = updateTitle(
					selectedMotivo.detalles,
				);
			}
		}
	}, [fields.motivo, fields.chequeoSeguridad]);

	const defaultSteps: Steps[] = [
		{
			title: (
				<>
					<Text bold>¿Por qué motivo</Text> quieres{' '}
					<span style={{ whiteSpace: 'nowrap' }}>
						visitarnos?
						<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
					</span>
					{nextScreenIndex !== null &&
					nextScreenIndex === 1 ? null : (
						<Container
							padding={{ top: ContainerPadding.static200 }}
						>
							<Text appearance={TokenTextAppearance.copy200}>
								Selecciona la razón principal de tu visita.
							</Text>
						</Container>
					)}
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<MultipleCTASelection
							ctaValues={CTAsMotivo}
							name="motivo"
							nextOnClick
						/>
					}
				/>
			),
			hidePreviousStep: false,
			isFirstStep: true,
			screenIndex: CitaPosventaSteps.Motivo,
			name: 'Motivo',
			ctas: CTAsMotivo,
		},
		{
			title: (
				<>
					<Text>
						¿Nos puedes indicar{' '}
						<Text bold>tu número The Originals?</Text>
					</Text>
					<Container padding={{ top: ContainerPadding.static200 }}>
						<Text appearance={TokenTextAppearance.copy200}>
							Encontrarás tu número The Originals en el QR que
							puedes descargar desde tu perfil.
						</Text>
					</Container>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<InputRegex
							name="miembroTheOriginals"
							regex={/^TH\d{6}$/i}
							length={8}
							label="Tu número"
							errorLabel="Número incorrecto"
							required
						/>
					}
				/>
			),
			screenIndex: CitaPosventaSteps.MiembroTheOriginals,
			name: 'MiembroTheOriginals',
		},
		{
			title: (
				<>
					<Text>
						¿Quieres realizar un{' '}
						<Text bold>chequeo de seguridad gratuito?</Text>
					</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<MultipleCTASelection
							ctaValues={CTAsSiNo}
							name="chequeoSeguridad"
							ctaWidth="81"
							nextOnClick
						/>
					}
				/>
			),
			screenIndex: CitaPosventaSteps.ChequeoSeguridad,
			name: 'ChequeoSeguridad',
		},
		{
			title: getMotivoTitle(),
			fields: <LayoutTemplate element={<DetalleMotivo />} />,
			screenIndex: CitaPosventaSteps.MasInformacion,
			name: 'MasInformacion',
			outputs: ['masInformacion'],
			outputsText: ['masInformacion'],
		},
		{
			screenIndex: CitaPosventaSteps.Mapa,
			name: 'Mapa',
			multiStep: true,
			component:
				isCitaPosventaDealer ||
				(isCitaPosventaEmbeddedPortal && selectedDealer) ? (
					<MapaCitaDealer />
				) : (
					<MapaCita />
				),
		},
		{
			title: (
				<>
					Para poder preparar tu visita, necesitamos saber{' '}
					<Text bold>quién eres</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<div>
							<NombreApellidos />
						</div>
					}
				/>
			),
			screenIndex: CitaPosventaSteps.NombreApellido,
			name: 'NombreApellido',
			outputs: ['name', 'surname', 'secondSurname'],
			outputsText: ['name', 'surname', 'secondSurname'],
		},
		{
			title: (
				<>
					Cuéntanos más sobre tu coche.{' '}
					<Text bold>¿Qué matrícula tiene y qué modelo es?</Text>
				</>
			),
			fields: (
				<LayoutTemplate element={<MatriculaAnoMatriculacionModelo />} />
			),
			screenIndex: CitaPosventaSteps.Matricula,
			name: 'Matricula',
			outputs: ['modelo', 'matricula'],
			outputsText: ['modelo', 'con matrícula', 'matricula'],
		},
		{
			title: (
				<>
					Y, ¿cuántos <Text bold>kilómetros?</Text>
					<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
				</>
			),
			fields: <Kilometros />,
			screenIndex: CitaPosventaSteps.Kilometros,
			name: 'Kilometros',
			outputs: ['kilometros'],
			outputsText: ['kilometros'],
		},
		{
			title: (
				<>
					En caso de necesitar contactar contigo,{' '}
					<Text bold>¿a qué número podemos llamarte?</Text>
				</>
			),
			fields: <LayoutTemplate element={<Telefono required={true} />} />,
			screenIndex: CitaPosventaSteps.Telefono,
			name: 'Telefono',
			outputs: ['cellphone'],
			outputsText: ['cellphone'],
		},

		{
			title: (
				<>
					Y,{' '}
					<Text bold>
						¿a qué email te enviamos la confirmación y los datos de
						tu cita?
					</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<div>
							<Email required />
						</div>
					}
				/>
			),
			screenIndex: CitaPosventaSteps.Email,
			name: 'Email',
			outputs: ['email'],
			outputsText: ['email'],
		},
		{
			title: (
				<>
					<Text bold>¿Necesitarás otro coche</Text> mientras el tuyo
					está en el{' '}
					<span style={{ whiteSpace: 'nowrap' }}>
						taller?
						<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
					</span>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<>
							<MultipleCTASelection
								ctaValues={CTAsSiNo}
								name="cocheSustitucion"
								ctaWidth="81"
								nextOnClick
							/>
							<Container
								padding={{ top: ContainerPadding.static500 }}
							>
								<Text
									appearance={TokenTextAppearance.copy100}
									textAlign={TextAlignment.left}
									staticSize
								>
									<Text bold>Coche de sustitución:</Text> este
									servicio puede tener un coste asociado. El
									Servicio Oficial contactará contigo para
									informarte de la disponibilidad y
									condiciones de este servicio.
								</Text>
							</Container>
						</>
					}
				/>
			),
			screenIndex: CitaPosventaSteps.CocheSustitucion,
			name: 'CocheSustitucion',
			outputs: ['cocheSustitucion'],
			outputsText: ['cocheSustitucion', 'quiero otro coche'],
			trueOrFalseQuestion: true,
		},
		{
			title: (
				<>
					Y para facilitar tu visita,{' '}
					<Text bold>
						¿llevamos tu coche al{' '}
						<span style={{ whiteSpace: 'nowrap' }}>
							taller?
							<sup style={{ top: 'auto', fontSize: '100%' }}>
								*
							</sup>
						</span>
					</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<>
							<MultipleCTASelection
								ctaValues={CTAsSiNo}
								name="cocheAlTaller"
								ctaWidth="81"
								nextOnClick
							/>
							<Container
								padding={{ top: ContainerPadding.static500 }}
							>
								<Text
									appearance={TokenTextAppearance.copy100}
									textAlign={TextAlignment.left}
									staticSize
								>
									<Text bold>Recogida a domicilio:</Text> este
									servicio puede tener un coste asociado. El
									Servicio Oficial contactará contigo para
									informarte de la disponibilidad y
									condiciones de este servicio.
								</Text>
							</Container>
						</>
					}
				/>
			),
			screenIndex: CitaPosventaSteps.CocheAlTaller,
			name: 'CocheAlTaller',
			outputs: ['cocheAlTaller'],
			outputsText: [
				'cocheAlTaller',
				'quiero servicio de recogida y entrega',
			],
			trueOrFalseQuestion: true,
		},
		{
			title: <></>,
			fields: <ConfirmacionCita />,
			screenIndex: CitaPosventaSteps.Confirmacion,
			name: 'Confirmacion',
			isLastStep: true,
			hidePreviousStep: true,
		},
	];

	const getLssiTmk = (value: string) => {
		switch (value) {
			case 'PERMITE':
				return 'PERMITIDO';
				break;
			case 'NO PERMITE':
				return 'ROBINSON';
				break;
			default:
				return null;
		}
	};

	const getModelo = (fields: FormDataFields, carlines: CarlineBase[]) => {
		if (fields.idBuzzExclusive && carlines?.length > 0) {
			return carlines.find(carline => carline.name.includes('Buzz'));
		}
		return fields.modeloList
			? fields.modeloList
			: carlines?.length > 0 && fields.modelo
			? carlines.find(carline => carline.name === fields.modelo)
			: {
					code: null,
					key: null,
					name: null,
			  };
	};

	const getInstallationAdress = markerInfo => {
		return (
			markerInfo.dealerInfo?.address +
			' ' +
			markerInfo.dealerInfo?.zipCode +
			' ' +
			markerInfo.dealerInfo?.city +
			' ' +
			markerInfo.dealerInfo?.province
		);
	};

	const createAppointmentDealer = (dealer: DealersData) => {
		const { markerInfo, dmsInfo } = dealer;

		return {
			assessors: dmsInfo.assessors ? [...dmsInfo.assessors] : undefined,
			dealerId: dmsInfo.dealerId,
			dmsAvailable: dmsInfo.dmsAvailable ? dmsInfo.dmsAvailable : false,
			email: dmsInfo.email ? dmsInfo.email : markerInfo.dealerInfo.mail,
			name: dmsInfo.name ? dmsInfo.name : markerInfo.dealerInfo.name,
			phone: dmsInfo.phone ? dmsInfo.phone : markerInfo.dealerInfo.phone,
			overhaulingServiceAvialable: dmsInfo.overhaulingServiceAvialable
				? dmsInfo.overhaulingServiceAvialable
				: false,
			pickupCarServiceAvailabe: dmsInfo.pickupCarServiceAvailabe
				? dmsInfo.pickupCarServiceAvailabe
				: false,
			replacementCarServiceAvailable:
				dmsInfo.replacementCarServiceAvailable
					? dmsInfo.replacementCarServiceAvailable
					: false,
			reference: dmsInfo.reference,
		};
	};

	const sendForm = async (): Promise<boolean> => {
		return new Promise(async resolve => {
			const { tc, today, device } = getParamsForm();
			const { formData, dealersInfo } = store.getState();

			const { fields } = formData;
			const { citaCarlines } = dealersInfo;
			const { dealer } = fields;
			const {
				dmsInfo,
				markerInfo,
			}: { dmsInfo: DmsInfo; markerInfo: DealerInfo } = dealer;
			const modelo = getModelo(fields, citaCarlines);
			const appointmentDealer = createAppointmentDealer(dealer);
			const appointmentDate = dayjs(fields.slot.from).format(
				'DD/MM/YYYY HH:mm:ss',
			);

			const selectedMotivo = CTAsMotivo.find(
				ctaMotivo => ctaMotivo.value === fields.motivo,
			);
			const info = isFakeMotivo(fields.motivo)
				? selectedMotivo?.label ?? ''
				: fields.masInformacion ?? '';
			const miembro =
				isComerciales && fields.miembroTheOriginals
					? ' ' + fields.miembroTheOriginals.toUpperCase()
					: '';
			const chequeo =
				fields.chequeoSeguridad === true ||
				fields.chequeoSeguridad === 'true'
					? ' con chequeo de seguridad gratuito solicitado'
					: '';
			const comments = info + miembro + chequeo;

			const appointmentNode = {
				appointment: {
					comments: comments,
					customer: {
						contactPhone: `${formatPrefix(fields.prefix)}${
							fields.cellphone
						}`,
						customerType: 'F',
						documentNumber: null,
						documentType: null, // Parametrizar [DNI,NIF,NIE] || null
						email: removeWhiteSpace(fields.email),
						gender: 'M', // Parametrizar [M,V]
						name: fields.name?.trim(),
						owner: false,
						surname: fields.surname?.trim(),
						surname2: fields.secondSurname?.trim(),
					},
					vehicle: fields.vehicleForMaintenance
						? fields.vehicleForMaintenance
						: {
								brand: groupBrand,
								frame: null,
								millage: Array.isArray(fields.kilometros)
									? fields.kilometros[0]
									: fields.kilometros,
								modelVersion: 'NO INFORMADO',
								modelYear: 0,
								plate: fields.matricula?.toUpperCase(),
								// countryPlate: fields.countryPlate,
								plateDate: `${fields.anoMatriculacion}-01-01 00:00:00`,
								tmaimg: null,
								model: modelo,
						  },
					dealer: appointmentDealer,
					slot: {
						from: fields.slot.from,
						to: fields.slot.to,
						replacementCarServiceAvailable:
							fields.slot.replacementCarServiceAvailable,
						dayMoment: fields.slot.dayMoment,
						assessorCode: fields.slot.assessorCode,
					},
					assessor: {
						code: fields.assessor.code,
						name: fields.assessor.name,
					},
					intervention: {
						code: getFilteredValueMotivo(fields.motivo),
						name: getFilteredLabelMotivo(fields.motivo),
					},
					lopd: 'PERMITIDO',
					lssi: getLssiTmk(fields.lssi),
					pickupServiceRequired:
						typeof fields.cocheAlTaller === 'string'
							? JSON.parse(fields.cocheAlTaller)
							: undefined,
					replacementCarServiceRequired:
						typeof fields.cocheSustitucion === 'string'
							? JSON.parse(fields.cocheSustitucion)
							: undefined,
					status: 'P',
					tmk: getLssiTmk(fields.tmk),
					sms: null,
				},
			};

			const surveyNode = null; // Flag para que Eapi no grabe survey
			/*const surveyNode = {
				survey: {
					dealer: {
						dmsInfo: dmsInfo,
						markerInfo: markerInfo,
					},
					deviceType: getDeviceType(),
					formId: `vw-oneform-${fields.formName}`,
					formVersion: `vw-oneform-${fields.formName}-${process.env.VERSION}`,
					tc: getTrackingCode(),
					url,
					urlOrigin: document.referrer || null,
					deepLink: fields.deepLink,
					budget: fields.budget,
					waid,
				},
			};*/

			const eapiData: CitaFormDataFields = {
				group_brand: groupBrand,
				kvps: dealer.kvps,
				cod_instalacion: dealer.codInstalacion,
				...appointmentNode,
				...surveyNode,
			};

			const eapiDataToSend = new FormData();
			for (const key in eapiData) {
				if (typeof eapiData[key] === 'object') {
					eapiDataToSend.append(key, JSON.stringify(eapiData[key]));
				} else {
					eapiDataToSend.append(key, eapiData[key]);
				}
			}

			// Crear EAPI solo cita con DMS
			const eapiResponse = appointmentDealer.dmsAvailable
				? await createEapiAppointment(eapiDataToSend)
				: null;
			const appointmentPincode =
				eapiResponse?.data?.content?.pincode ?? null;

			// Registro DPS si EAPI ok o cita sin DMS
			if (appointmentPincode || !appointmentDealer.dmsAvailable) {
				const ndpsData = {
					'headers[DPS_TOKEN]': isComerciales
						? 'LCV_DDB_FRM_SERVICE_APPOINTMENT'
						: 'VW_DDB_FRM_SERVICE_APPOINTMENT',
					'headers[FK_SYSTEM]': isComerciales
						? 'LCV_DDB_FRM_SERVICE_APPOINTMENT'
						: 'VW_DDB_FRM_SERVICE_APPOINTMENT',
					'headers[FORM_ID]': wa,
					'headers[FORM_NAME]': `vw-oneform-${fields.formName}`,
					'headers[FORM_VERSION]': `vw-oneform-${fields.formName}-${process.env.VERSION}`,
					'headers[FORM_DATE_INSERTION]': today,
					'headers[FORM_OBJECTIVE]': 'Cita Posventa',
					'headers[ORIGIN_DEVICE_TYPE]': device,
					'headers[ORIGIN_TRACKING_CODE]': tc,

					'fields[DPS_BRAND]': isComerciales
						? 'Volkswagen CV'
						: 'Volkswagen',

					'fields[PERSON_NAME]': fields.name,
					'fields[PERSON_SURNAME_1]': fields.surname,
					'fields[PERSON_SURNAME_2]': fields.secondSurname,
					'fields[PERSON_EMAIL]': fields.email,
					'fields[PERSON_PHONE_MOBILE]': fields.cellphone,
					'fields[PERSON_RGPD_PERFILADO]': fields.tmk
						? fields.tmk
						: 'NO INFORMADO',
					'fields[PERSON_RGPD_COM_COMERCIALES]': fields.lssi
						? fields.lssi
						: 'NO INFORMADO',
					'fields[COMMENTS_PERSON]': comments,
					'fields[CREATIVITY_PERSONALIZATION_DATA]': fields.budget,
					//'fields[]': fields.deepLink,

					'fields[SURVEY_ANSWER_01]': appointmentPincode,

					'fields[VEH_ACTUAL_MODEL]':
						appointmentNode.appointment.vehicle.model?.name,
					'fields[VEH_ACTUAL_PLATE]':
						appointmentNode.appointment.vehicle.plate,
					'fields[VEH_ACTUAL_PLATE_DATE]': transformDate(
						appointmentNode.appointment.vehicle.plateDate,
						'DD/MM/YYYY HH:mm:ss',
						'YYYY-MM-DD HH:mm:ss',
					),
					'fields[VEH_ACTUAL_MILEAGE]':
						appointmentNode.appointment.vehicle.millage,
					'fields[VEH_ACTUAL_FRAME]':
						appointmentNode.appointment.vehicle.frame,

					'fields[APPOINTMENT_TYPE]': 'NUEVA',
					'fields[APPOINTMENT_INSTALLATION_NAME]':
						appointmentDealer.name,
					'fields[APPOINTMENT_INSTALLATION_CODE]':
						appointmentDealer.dealerId?.codInstalacion,
					'fields[APPOINTMENT_INSTALLATION_ADDRESS]':
						getInstallationAdress(markerInfo),
					'fields[APPOINTMENT_INSTALLATION_EMAIL]':
						appointmentDealer.email,
					'fields[APPOINTMENT_INSTALLATION_PHONE]':
						appointmentDealer.phone?.replace(/\s+/g, ''),
					'fields[APPOINTMENT_REQUESTED_TYPE]':
						getFilteredValueMotivo(fields.motivo),
					'fields[APPOINTMENT_DATE]': appointmentDate,
					'fields[PERSON_PREFERRED_SLOT]':
						fields.slot.dayMoment?.code, // ej: MEDIODIA
					'fields[APPOINTMENT_COMMERCIAL_ADVISER]':
						fields.assessor.name,

					'flags[APPOINTMENT_FLAG_PICK_UP_SERVICE]':
						typeof fields.cocheAlTaller === 'string'
							? JSON.parse(fields.cocheAlTaller)
							: '',
					'flags[APPOINTMENT_FLAG_SUBSTITUTION_VEHICLE]':
						typeof fields.cocheSustitucion === 'string'
							? JSON.parse(fields.cocheSustitucion)
							: '',
					'flags[APPOINTMENT_FLAG_HAS_DMS]':
						appointmentDealer.dmsAvailable,
					'flags[PERSON_RGPD_ADAPTED]': 'true',

					'flags[FLAG_RELACION_MARCA_SIN_IC]': 'true',
					'flags[FLAG_IS_MYSTERY_LEAD]': 'false',
				};

				const ndpsDataToSend = formDataBuilder(
					JSON.parse(JSON.stringify(ndpsData)),
				);
				const ndpsResponse = await oneformBFFNdpsSend(ndpsDataToSend);
				if (ndpsResponse?.data?.status?.code == 200) {
					dispatch({
						type: 'SET_FORM_RESPONSE',
						payload: ndpsResponse.data,
					});
					return resolve(true);
				} else if (appointmentPincode) {
					// Eliminar EAPI si DPS ko
					const eapiDelete = {
						group_brand: groupBrand,
						pincode: appointmentPincode,
					};
					await deleteEapiAppointment(formDataBuilder(eapiDelete));
					resolve(false);
				}
			} else if (!appointmentPincode && appointmentDealer.dmsAvailable) {
				dispatch({ type: 'SET_SEND_DMS_FAILED', payload: true });
				resolve(false);
			} else {
				resolve(false);
			}
		});
	};

	const repeatKvpsRequest = async (kvps: string) => {
		const newSelectedDealer = await getDealerKvps(kvps);

		if (newSelectedDealer) {
			setCitaPosventaData({
				...citaPosventaData,
				fields: {
					...citaPosventaData.fields,
					dealer: newSelectedDealer,
				},
			});
			// CitaPosventaData.fields.dealer = newSelectedDealer;
		}
	};

	const getFinalSteps = (
		steps: Steps[],
		motivo: string,
		observaciones: string,
	) => {
		let newSteps = [...steps];

		newSteps = spliceSteps(newSteps, motivo, 'Motivo');
		newSteps = spliceSteps(newSteps, observaciones, 'MasInformacion');

		if (
			faContent?.motivo &&
			!isPlacaTheOriginalsMotivo(faContent?.motivo)
		) {
			newSteps = removeStep(newSteps, 'MiembroTheOriginals');
			newSteps = removeStep(newSteps, 'ChequeoSeguridad');
		}

		const finalSteps = resetStepsScreenIndex(newSteps);

		return finalSteps;
	};

	/**
	 * Dependiendo del journey que siga el usuario se empezará en una pantalla o en otra, pero hay que adaptar stepHistory para que aun así pueda volver hacia atrás.
	 */
	const getFirstStepAndStepsHistory = (): {
		firstStep: Steps;
		stepsHistory: number[];
		finalSteps: Steps[];
	} => {
		// Obtención de los parametros de la url
		let selectedMotivo = null;
		const observaciones = faContent?.observaciones;

		if (
			isCitaPosventaEmbeddedCalculadora ||
			isCitaPosventaEmbeddedMaintenance
		) {
			// En los forms de Calculadora y Mi Mantenimieto nos saltamos algunos pasos
			const stepsWithoutMap = removeMultipleStepsAndResetScreenIndex(
				defaultSteps,
				[
					'Motivo',
					'MiembroTheOriginals',
					'ChequeoSeguridad',
					'MasInformacion',
					'Matricula',
					'Kilometros',
				],
			);

			return {
				finalSteps: stepsWithoutMap,
				stepsHistory: [0],
				firstStep: stepsWithoutMap[0],
			};
		}

		if (isCitaPosventaEmbeddedPortal) {
			// En los forms de Calculadora y Mi Mantenimieto nos saltamos algunos pasos
			const stepsWithoutMap = removeMultipleStepsAndResetScreenIndex(
				defaultSteps,
				[
					faContent.motivo && 'Motivo',
					'MiembroTheOriginals',
					'ChequeoSeguridad',
					'Matricula',
					'Kilometros',
				],
			);

			return {
				finalSteps: stepsWithoutMap,
				stepsHistory: [0],
				firstStep: stepsWithoutMap[0],
			};
		}

		if (isCitaPosventaEmbeddedBonos) {
			const stepsWithoutMap = removeMultipleStepsAndResetScreenIndex(
				defaultSteps,
				[
					'Motivo',
					'MiembroTheOriginals',
					'ChequeoSeguridad',
					'MasInformacion',
				],
			);

			return {
				finalSteps: stepsWithoutMap,
				stepsHistory: [0],
				firstStep: stepsWithoutMap[0],
			};
		}

		if (window && window.location) {
			const urlParams = new URLSearchParams(window.location.search);
			const motivoParam = urlParams.get('motivo');
			const dealerParam = urlParams.get('dealer');

			if (motivoParam) {
				selectedMotivo = CTAsMotivo.find(ctaMotivo => {
					return ctaMotivo.value === motivoParam;
				});
			}
			if (dealerParam && selectedDealer) {
				// CitaPosventaData.fields.dealer = selectedDealer;
				setCitaPosventaData({
					...citaPosventaData,
					fields: {
						...citaPosventaData.fields,
						dealer: selectedDealer,
					},
				});
			} else if (dealerParam && !selectedDealer) {
				// En caso de que por algun motivo haya dealer pero no se haya cargado correctamente en el cms repetimos la request
				repeatKvpsRequest(dealerParam);
			}
		}

		const finalSteps = getFinalSteps(
			defaultSteps,
			faContent?.motivo ? faContent.motivo : fields.motivo,
			observaciones,
		);

		const defaultFirstStep = finalSteps[CitaPosventaSteps.Motivo];

		let firstStep = finalSteps.find((step: Steps) => {
			if (selectedMotivo && (!faContent || !faContent.motivo)) {
				return step.screenIndex === CitaPosventaSteps.MasInformacion;
			} else {
				return step.screenIndex === defaultFirstStep.screenIndex;
			}
		});

		firstStep = firstStep ? firstStep : defaultFirstStep;
		firstStep.isFirstStep = true;

		let stepsHistory = [0];
		if (firstStep.screenIndex === defaultFirstStep.screenIndex) {
			stepsHistory = [0];
		} else {
			stepsHistory = [0, firstStep.screenIndex];
		}

		const motivoData = faContent?.motivo
			? faContent.motivo
			: selectedMotivo
			? selectedMotivo.value
			: null;
		const masInformacionData = faContent?.observaciones
			? faContent.observaciones
			: isFakeMotivo(motivoData)
			? selectedMotivo?.detalles
			: null;

		setCitaPosventaData({
			...citaPosventaData,
			fields: {
				...citaPosventaData.fields,
				motivo: motivoData,
				masInformacion: masInformacionData,
			},
		});

		return { firstStep, stepsHistory, finalSteps };
	};

	const handleStepsOfCitaDealer = (steps: Steps[], dealer: DealersData) => {
		let newSteps = [...steps];

		if (isComerciales) {
			return;
		}

		const m = faContent?.motivo ? faContent.motivo : fields.motivo;

		if (
			!dealer.dmsInfo?.pickupCarServiceAvailabe ||
			isPlacaTheOriginalsMotivo(m)
		) {
			newSteps = removeMultipleStepsAndResetScreenIndex(newSteps, [
				'CocheAlTaller',
			]);
		}
		if (
			!dealer.dmsInfo?.replacementCarServiceAvailable ||
			isPlacaTheOriginalsMotivo(m)
		) {
			newSteps = removeMultipleStepsAndResetScreenIndex(newSteps, [
				'CocheSustitucion',
			]);
		}

		setSteps(newSteps);
		setFirstStep(newSteps[0]);
		setStepsHistory(stepsHistory);
		setLoading(false);
	};

	const getSteps = (steps: Steps[]) => {
		if (isCitaPosventaDealer && selectedDealer) {
			handleStepsOfCitaDealer(defaultSteps, selectedDealer);
			return;
		}
		const { firstStep, stepsHistory, finalSteps } =
			getFirstStepAndStepsHistory();
		setSteps(finalSteps);
		setFirstStep(firstStep);
		setStepsHistory(stepsHistory);
		setLoading(false);
	};

	useEffect(() => {
		getSteps(defaultSteps);
		getEapiCarlines().then(response => {
			dispatch({
				type: 'SET_CITA_CARLINES',
				payload: response.data?.content,
			});
		});
	}, []);

	return errorDealers ? (
		<Error />
	) : loading || loadingDealers ? (
		<Loading />
	) : userIsAuthenticated && useLoggedJourney ? (
		<NuevaCitaPosventaFormLogged
			defaultSteps={steps}
			sendForm={sendForm}
			firstStep={firstStep}
			CitaPosventaData={citaPosventaData}
		/>
	) : (
		<NuevaCitaPosventaFormDefault
			defaultSteps={steps}
			sendForm={sendForm}
			firstStep={firstStep}
			CitaPosventaData={citaPosventaData}
			stepsHistory={stepsHistory}
		/>
	);
};
